import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useDashboard } from "../../../../context/dashboardContext";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";

const UserFormModal = ({ isOpen, onClose, refreshData }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ user: "", email: "", password: "", });
  const { CreateUser } = useDashboard();
  const [showPassword, setShowPassword] = useState(false);

  // Reset form fields every time modal opens
  useEffect(() => {
    if (isOpen) { setFormData({ user: "", email: "", password: "", }); }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.trim().length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);

    try {
      const result = await CreateUser(formData);
      if (result.success) {
        toast.success("The user has been successfully created");
        onClose();
        refreshData();
      } else {
        toast.error("The user could not be created");
      }
    } catch (err) {
      toast.error("An error occurred during user creation");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-xl p-6 w-full max-w-md relative shadow-lg transform transition-all duration-300 scale-95 animate-fadeIn">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-colors" >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800 border-b pb-3">
          Add New User
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User
            </label>
            <input type="text" name="user" value={formData.user} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 transition" required autoComplete="new-user" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 transition" required autoComplete="new-email" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 transition" required autoComplete="new-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700" >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
           <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-lg font-semibold shadow-sm transition-all duration-300 transform
              ${loading
                ? "bg-gray-400 text-gray-700 cursor-not-allowed opacity-70"
                : "bg-gradient-to-r from-red-600 to-red-800 text-white hover:scale-105 hover:shadow-lg hover:from-red-700 hover:to-red-900"
              }`}
          >
            {loading ? "Creating..." : "Submit"}
          </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default UserFormModal;